import React, { useState } from 'react';

const FORMATS = [
  { id: 'ip:port',      label: 'IP:Port',           desc: '192.168.1.1:8080' },
  { id: 'full',         label: 'Full DSN',           desc: 'proto://ip:port' },
  { id: 'ip:port:user:pass', label: 'IP:Port:User:Pass', desc: 'ip:port:user:pass' },
];

const FILTERS = [
  { id: 'all',    label: '🌐 All',       emoji: '🌐' },
  { id: 'alive',  label: '✅ Alive',     emoji: '✅' },
  { id: 'dead',   label: '❌ Dead',      emoji: '❌' },
  { id: 'http',   label: '🔵 HTTP',      emoji: '🔵' },
  { id: 'https',  label: '🔷 HTTPS',     emoji: '🔷' },
  { id: 'socks4', label: '🟣 SOCKS4',    emoji: '🟣' },
  { id: 'socks5', label: '🟣 SOCKS5',    emoji: '🟣' },
  { id: 'elite',  label: '🌟 Elite',     emoji: '🌟' },
  { id: 'anon',   label: '😎 Anonymous', emoji: '😎' },
];

function buildLine(proxy, result, format) {
  const ip   = result?.ip   || '';
  const port = result?.port || proxy.split(':')[1] || '';
  if (format === 'ip:port') return ip && port ? `${ip}:${port}` : null;
  if (format === 'full') {
    const proto = result?.protocol || 'http';
    return ip && port ? `${proto}://${ip}:${port}` : null;
  }
  if (format === 'ip:port:user:pass') {
    const parts = proxy.replace(/^[^@]+:\/\//, '').split(':');
    const user  = parts[2] || '';
    const pass  = parts[3] || '';
    return ip && port ? `${ip}:${port}${user ? `:${user}:${pass}` : ''}` : null;
  }
  return null;
}

function filterResults(proxyList, resultsMap, filterId) {
  return proxyList.reduce((acc, proxy, i) => {
    const result = resultsMap.get(i);
    if (!result || result.checking) return acc;
    const alive = result.ip && !result.error;
    const proto = (result.protocol || '').toLowerCase();
    const anon  = (result.anonymity || '').toLowerCase();
    let pass = false;
    if (filterId === 'all')    pass = true;
    else if (filterId === 'alive')  pass = !!alive;
    else if (filterId === 'dead')   pass = !alive;
    else if (filterId === 'http')   pass = proto === 'http';
    else if (filterId === 'https')  pass = proto === 'https';
    else if (filterId === 'socks4') pass = proto === 'socks4';
    else if (filterId === 'socks5') pass = proto === 'socks5' || proto === 'socks5h';
    else if (filterId === 'elite')  pass = anon === 'elite';
    else if (filterId === 'anon')   pass = anon === 'anonymous';
    if (pass) acc.push({ proxy, result, index: i });
    return acc;
  }, []);
}

export default function ExportPanel({ proxyList, resultsMap }) {
  const [activeFilter, setActiveFilter] = useState('alive');
  const [activeFormat, setActiveFormat] = useState('ip:port');
  const [copied, setCopied]             = useState(false);

  const done = [...resultsMap.values()].filter(r => r && !r.checking).length;
  if (done === 0) return null;

  const filtered = filterResults(proxyList, resultsMap, activeFilter);
  const lines    = filtered.map(({ proxy, result }) => buildLine(proxy, result, activeFormat)).filter(Boolean);
  const text     = lines.join('\n');

  const handleCopy = async () => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!text) return;
    const blob = new Blob([text], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `meowproxy_${activeFilter}_${activeFormat.replace(/:/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-orange-100 rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-orange-50 bg-gradient-to-r from-orange-50 to-pink-50 flex items-center justify-between">
        <h3 className="text-slate-700 font-bold flex items-center gap-2 text-sm">
          <span className="text-base">📤</span> Export Results
        </h3>
        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-orange-100 text-orange-500 border border-orange-200">
          {lines.length} proxies
        </span>
      </div>

      <div className="p-5 flex flex-col gap-5">
        {/* Filter row */}
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-2">Filter by</p>
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map(f => (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className={`px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all duration-150 ${
                  activeFilter === f.id
                    ? 'bg-orange-400 text-white border-orange-400 shadow-sm shadow-orange-200'
                    : 'bg-orange-50 text-orange-400 border-orange-200 hover:bg-orange-100'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Format row */}
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-2">Format</p>
          <div className="flex flex-wrap gap-2">
            {FORMATS.map(f => (
              <button
                key={f.id}
                onClick={() => setActiveFormat(f.id)}
                className={`flex flex-col px-4 py-2.5 rounded-xl border text-left transition-all duration-150 ${
                  activeFormat === f.id
                    ? 'bg-orange-400 text-white border-orange-400 shadow-sm shadow-orange-200'
                    : 'bg-orange-50 text-orange-400 border-orange-200 hover:bg-orange-100'
                }`}
              >
                <span className="text-[11px] font-bold">{f.label}</span>
                <span className={`text-[10px] font-mono mt-0.5 ${activeFormat === f.id ? 'text-white/80' : 'text-slate-400'}`}>{f.desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-2">Preview</p>
          <div className="bg-orange-50/60 border border-orange-100 rounded-xl p-3 font-mono text-[11px] text-slate-500 max-h-28 overflow-y-auto custom-scrollbar leading-5">
            {lines.length > 0
              ? lines.slice(0, 8).map((l, i) => <div key={i}>{l}</div>)
              : <span className="text-slate-300 italic">No proxies match this filter…</span>
            }
            {lines.length > 8 && (
              <div className="text-orange-400 font-bold mt-1">+ {lines.length - 8} more…</div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleCopy}
            disabled={lines.length === 0}
            className={`flex-1 py-3 rounded-2xl font-extrabold text-sm flex items-center justify-center gap-2 transition-all duration-200 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed ${
              copied
                ? 'bg-emerald-400 text-white shadow-emerald-200'
                : 'bg-gradient-to-r from-orange-400 to-pink-400 text-white shadow-orange-200 hover:shadow-orange-300 hover:-translate-y-0.5'
            }`}
          >
            {copied ? '✅ Copied!' : '📋 Copy to Clipboard'}
          </button>
          <button
            onClick={handleDownload}
            disabled={lines.length === 0}
            className="flex-1 py-3 rounded-2xl font-extrabold text-sm flex items-center justify-center gap-2 bg-white border-2 border-orange-200 text-orange-400 hover:bg-orange-50 hover:border-orange-300 transition-all duration-200 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed hover:-translate-y-0.5"
          >
            💾 Download .txt
          </button>
        </div>
      </div>
    </div>
  );
}